import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Ban, Search, Filter, Download, MoreVertical, CheckCircle2, XCircle, Users, Wallet } from 'lucide-react';
import { agenciesAPI } from '../utils/api';
import constant from '../constants/constant';

const AgenciesManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      const response = await agenciesAPI.getAgencies();
      setAgencies(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError('Failed to load agencies. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const filteredAgencies = Array.isArray(agencies) ? agencies.filter(agency =>
    (agency.agency_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (agency.creator_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (agency.bd_code?.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const baseUrl = constant.API_BASE_URL.replace('/api/', '');
    return `${baseUrl}${path}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">Agencies Management</h2>
          <p className="text-gray-500 mt-1">Monitor and manage all registered agencies and their hosts.</p>
        </div>

        <div className="flex gap-3">
          <button className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition">
            <Download size={18} /> Export
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-[20px] shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search agencies..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition text-sm font-medium">
              <Filter size={16} /> Filters
            </button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-500">Loading agencies...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchAgencies} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Agency Details</th>
                  <th className="p-6">Owner</th>
                  <th className="p-6">BD Code</th>
                  <th className="p-6">BD Name</th>
                  <th className="p-6">Hosts</th>
                  <th className="p-6 text-center">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAgencies.map((agency) => (
                  <tr key={agency.id} className="hover:bg-gray-50/80 transition duration-150 group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 overflow-hidden border border-gray-100 shadow-inner flex items-center justify-center">
                          <div className="w-full h-full flex items-center justify-center text-slate-400 font-bold bg-slate-50">
                            {agency.agency_name?.[0]}
                          </div>
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-base">{agency.agency_name}</p>
                          <p className="text-xs text-gray-400">ID: #{agency.id?.substring(0, 8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-600 font-medium">
                      {agency.creator_name || 'N/A'}
                    </td>
                    <td className="p-6">
                      <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold">
                        {agency.bd_code || 'N/A'}
                      </span>
                    </td>
                    <td className="p-6 text-slate-600 font-medium">
                      {agency.bd_name || 'N/A'}
                    </td>
                    <td className="p-6">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-gray-400" />
                        <span className="font-bold text-slate-700">{agency.hosts_count || 0}</span>
                      </div>
                    </td>
                    <td className="p-6 text-center">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        agency.status === 'approved' ? 'bg-green-100 text-green-600' : 
                        agency.status === 'rejected' ? 'bg-red-100 text-red-600' : 
                        'bg-yellow-100 text-yellow-600'
                      }`}>
                        {agency.status || 'pending'}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => navigate(`/agencies/${agency.id}`)}
                          className="p-2 hover:bg-pink-50 text-gray-400 hover:text-pink-600 rounded-lg transition"
                        >
                          <Eye size={18} />
                        </button>
                        <button className="p-2 hover:bg-slate-100 text-gray-400 hover:text-slate-600 rounded-lg transition">
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAgencies.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-10 text-center text-gray-500">No agencies found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgenciesManagement;
